/**
 * Shared school-switcher state (ux-overhaul-hld.md §3).
 *
 * Same persistence pattern as CRM leads column config
 * (`simplePreferencesStore` + zustand `persist` under `user-preferences`):
 * the chosen school {id, name} is stored in the browser so close / re-login
 * restores it. School-level staff have one id in `/me.school_ids` — they
 * never switch; we still persist that school's name for the sidebar.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthV2Store } from '@/features/auth/authV2Store';
import { useSchools } from '@/features/academic/api';
import type { School } from '@/features/auth/types';
import {
  parsePersistedSelection,
  resolveSchoolContext,
  type SchoolOption,
} from './schoolSelectionLogic';

export {
  parsePersistedSelection,
  resolveSchoolContext,
  type SchoolOption,
} from './schoolSelectionLogic';

/** Zustand persist key — same mechanism as `user-preferences` for leads. */
export const SCHOOL_SELECTION_PERSIST_KEY = 'hw-school-selection';
/** Pre-persist key; still read once to migrate. */
export const SELECTED_SCHOOL_STORAGE_KEY = 'hw_selected_school';

export interface EffectiveSchoolContext {
  schoolId: string | undefined;
  selectedSchool: SchoolOption | undefined;
  isWildcardScope: boolean;
  isResolving: boolean;
  schools: SchoolOption[];
  canSwitch: boolean;
}

function readLegacySelection(): SchoolOption | null {
  try {
    return parsePersistedSelection(localStorage.getItem(SELECTED_SCHOOL_STORAGE_KEY));
  } catch {
    return null;
  }
}

function sameSchool(a: SchoolOption | null, b: SchoolOption | null): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.id === b.id && a.name === b.name && a.orgId === b.orgId;
}

function toSchoolOptions(schools: School[]): SchoolOption[] {
  return schools
    .map((school) => ({ id: school.id, name: school.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

interface SchoolSelectionState {
  selected: SchoolOption | null;
  schoolsCache: SchoolOption[];
  setSelectedSchool: (school: SchoolOption) => void;
  setSelectedSchoolId: (schoolId: string) => void;
  setSchoolsCache: (schools: SchoolOption[]) => void;
}

export const useSchoolSelectionStore = create<SchoolSelectionState>()(
  persist(
    (set, get) => ({
      selected: readLegacySelection(),
      schoolsCache: [],

      setSelectedSchool(school) {
        const current = get().selected;
        if (sameSchool(current, school)) return;
        if (!school.name && current?.id === school.id && current.name) return;
        set({ selected: school });
      },

      setSelectedSchoolId(schoolId) {
        const { schoolsCache, selected } = get();
        const named =
          schoolsCache.find((school) => school.id === schoolId) ??
          (selected?.id === schoolId ? selected : undefined);
        get().setSelectedSchool({
          id: schoolId,
          name: named?.name ?? '',
          orgId: named?.orgId ?? selected?.orgId,
        });
      },

      setSchoolsCache(schools) {
        const prev = get().schoolsCache;
        if (
          prev.length === schools.length &&
          prev.every((row, i) => row.id === schools[i]?.id && row.name === schools[i]?.name)
        ) {
          return;
        }
        set({ schoolsCache: schools });
      },
    }),
    {
      name: SCHOOL_SELECTION_PERSIST_KEY,
      partialize: (state) => ({
        selected: state.selected,
        schoolsCache: state.schoolsCache,
      }),
    },
  ),
);

/**
 * Effective school for the signed-in staff member.
 * GET /schools is allowed for any authenticated staff — including a teacher
 * with one campus — so the name can be cached for the next login.
 */
export function useSelectedSchoolId(): EffectiveSchoolContext {
  const rawSchoolIds = useAuthV2Store((s) => s.user?.school_ids);
  const orgId = useAuthV2Store((s) => s.user?.org_id);
  const hasUser = useAuthV2Store((s) => s.user != null);
  const persisted = useSchoolSelectionStore((s) => s.selected);
  const schoolsCache = useSchoolSelectionStore((s) => s.schoolsCache);
  const setSelectedSchool = useSchoolSelectionStore((s) => s.setSelectedSchool);
  const setSchoolsCache = useSchoolSelectionStore((s) => s.setSchoolsCache);

  const schoolsQuery = useSchools({ enabled: hasUser });
  const fetchedSchools = toSchoolOptions(schoolsQuery.data?.data ?? []);

  const resolved = resolveSchoolContext({
    schoolIds: rawSchoolIds,
    orgId,
    fetchedSchools,
    cachedSchools: schoolsCache,
    persisted,
  });

  useEffect(() => {
    if (fetchedSchools.length === 0) return;
    setSchoolsCache(fetchedSchools);
  }, [schoolsQuery.dataUpdatedAt, fetchedSchools, setSchoolsCache]);

  useEffect(() => {
    const next = resolved.selectedSchool;
    if (!next?.id || !next.name) return;
    const withOrg = orgId ? { ...next, orgId } : next;
    if (sameSchool(persisted, withOrg)) return;
    setSelectedSchool(withOrg);
  }, [resolved.selectedSchool, persisted, orgId, setSelectedSchool]);

  const hasCachedName = Boolean(resolved.selectedSchool?.name) || schoolsCache.length > 0;
  return {
    ...resolved,
    isResolving: hasUser && schoolsQuery.isLoading && fetchedSchools.length === 0 && !hasCachedName,
  };
}
